import { USER_ROLE } from '@/types/user-role';
import { getBrazilPhoneError } from '@/utils/brazil-phone';
import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres'),
});

const phoneValidator = z
  .string()
  .min(1, 'O telefone é obrigatório.')
  .superRefine((val, ctx) => {
    // Mesma regra da API (DDD existente + celular): sem isso o formulário
    // aceitava qualquer sequência de 10 dígitos.
    const message = getBrazilPhoneError(val);
    if (message) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    }
  })
  .transform((val) => val.replace(/\D/g, ''));

export const registerStep1Schema = z.object({
  role: z.nativeEnum(USER_ROLE, {
    errorMap: () => ({ message: 'Selecione o tipo de conta: prestador ou cliente.' }),
  }),
  username: z.string().min(3, 'O nome deve ter no mínimo 3 caracteres'),
  email: z.string().email('E-mail inválido'),
});

export const registerStep2Schema = z
  .object({
    password: z.string().min(8, 'A senha deve ter no mínimo 8 caracteres'),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

export const registerSchema = z
  .object({
    username: z.string().min(3, 'O nome deve ter no mínimo 3 caracteres'),
    email: z.string().email('E-mail inválido'),
    password: z.string().min(8, 'A senha deve ter no mínimo 8 caracteres'),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),

    role: z
      .nativeEnum(USER_ROLE, {
        // Toda falha aqui vira a mesma frase: a padrão do zod sai em inglês.
        errorMap: () => ({ message: 'Selecione o tipo de conta: prestador ou cliente.' }),
      })
      .optional(),

    // Prestador não preenche nada disso no cadastro: título cai para o nome,
    // telefone vem do onboarding do WhatsApp e bio fica para o perfil.
    providerProfile: z
      .object({
        title: z.string().optional(),
        businessPhone: z.string().optional(),
        bio: z.string().optional(),
        address: z.string().optional(),
      })
      .optional(),

    clientProfile: z
      .object({
        phone: phoneValidator,
        address: z.string().optional(),
      })
      .optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  })
  .refine((data) => data.role !== USER_ROLE.CLIENT || !!data.clientProfile, {
    message: 'O perfil é obrigatório',
    path: ['role'],
  });

export type LoginData = z.infer<typeof loginSchema>;
export type RegisterData = z.infer<typeof registerSchema>;
